import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getAdminSolutions } from '../../api/content';
import {
    getAdminPartners, createPartner, updatePartner, deletePartner, uploadPartnerLogo,
} from '../../api/partners';

/**
 * 파트너 관리 — 공개 페이지 `/partners` 의 내용.
 *
 * 구분은 넷이다 — 시스템 연동(POS·ERP·그룹웨어 회사), ESL 제조사, ESL 설치용품
 * (레일·집게·브라켓·스탠드), 고객 파트너(매장 설치를 전담하는 고객사).
 * 일반 고객은 넣지 않는다 — 한국 B2B 에서 '파트너사' 는 납품하는 쪽이라는 느낌이 강해,
 * 고객은 페이지 끝 문장으로 말한다. 정식 파트너 관계인 고객사만 '고객 파트너' 로 넣는다.
 *
 * 연동 솔루션은 목록에서 고른다(주소를 적지 않는다). 솔루션 주소가 바뀌어도 링크가
 * 따라간다. 비공개 솔루션을 고르면 공개 페이지에는 버튼이 안 나온다.
 */

const KINDS = [
    { value: 'system', label: '시스템 연동' },
    { value: 'manufacturer', label: 'ESL 제조사' },
    { value: 'fixture', label: 'ESL 설치용품' },
    { value: 'customer', label: '고객 파트너' },
];

const EMPTY = {
    kind: 'system', name: '', altName: '', slug: '', logoUrl: '', badge: '', tagline: '',
    summary: '', highlightsText: '', websiteUrl: '', solutionId: '', linkLabel: '', linkPath: '',
    isKey: false, sortOrder: 0, isPublished: true,
};

const toDraft = (p) => ({
    kind: p.kind, name: p.name, altName: p.altName || '', slug: p.slug, logoUrl: p.logoUrl || '',
    badge: p.badge || '', tagline: p.tagline, summary: p.summary || '',
    highlightsText: (p.highlights || []).join('\n'), websiteUrl: p.websiteUrl || '',
    solutionId: p.solutionId ?? '', linkLabel: p.linkLabel || '', linkPath: p.linkPath || '',
    isKey: p.isKey, sortOrder: p.sortOrder, isPublished: p.isPublished,
});

const toPayload = (d) => ({
    kind: d.kind,
    name: d.name,
    altName: d.altName,
    slug: d.slug,
    logoUrl: d.logoUrl,
    badge: d.badge,
    tagline: d.tagline,
    summary: d.summary,
    highlights: d.highlightsText.split('\n').map((s) => s.trim()).filter(Boolean),
    websiteUrl: d.websiteUrl,
    solutionId: d.solutionId === '' ? null : Number(d.solutionId),
    linkLabel: d.linkLabel,
    linkPath: d.linkPath,
    isKey: d.isKey,
    sortOrder: Number(d.sortOrder) || 0,
    isPublished: d.isPublished,
});

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const label = 'block text-xs font-bold text-gray-600 mb-1';

const Field = ({ title, hint, children }) => (
    <div>
        <span className={label}>{title}</span>
        {children}
        {hint && <p className="mt-1 text-[11px] text-gray-400 leading-snug">{hint}</p>}
    </div>
);

const Editor = ({ initial, editId, solutions, onClose, onSaved }) => {
    const [d, setD] = useState(initial);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const set = (k) => (e) => setD({ ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    // 주소 키가 비어 있으면 영문 쪽 이름에서 만들어 둔다(한글 이름이면 영문 표기에서).
    const suggestSlug = () => setD((prev) => (prev.slug ? prev : { ...prev, slug: slugify(prev.name) || slugify(prev.altName) }));

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true); setError('');
        try {
            const url = await uploadPartnerLogo(file);
            setD((prev) => ({ ...prev, logoUrl: url }));
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        setSaving(true); setError('');
        try {
            if (editId === 'new') await createPartner(toPayload(d));
            else await updatePartner(editId, toPayload(d));
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const highlightCount = d.highlightsText.split('\n').filter((s) => s.trim()).length;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <h3 className="text-lg font-extrabold text-gray-800 m-0">{editId === 'new' ? '파트너 추가' : '파트너 수정'}</h3>
                    <button type="button" onClick={onClose} aria-label="닫기" className="w-10 h-10 rounded-lg text-gray-500 hover:bg-gray-100">✕</button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {error && (
                        <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 whitespace-pre-line">{error}</div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <Field title="구분">
                            <select className={input} value={d.kind} onChange={set('kind')}>
                                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                            </select>
                        </Field>
                        <Field title="배지 (선택)" hint="카드 위의 짧은 표시. 예: 중소규모 프로젝트. '핵심 파트너'는 아래 체크로 자동 표시됩니다.">
                            <input className={input} value={d.badge} onChange={set('badge')} maxLength={40} />
                        </Field>
                        <Field title="이름 *">
                            <input className={input} value={d.name} onChange={set('name')} onBlur={suggestSlug} placeholder="올댓페이" maxLength={60} />
                        </Field>
                        <Field title="다른 표기 (선택)" hint="영문이나 한글 표기. 이름 옆에 작게 보이고 검색에도 걸립니다.">
                            <input className={input} value={d.altName} onChange={set('altName')} onBlur={suggestSlug} placeholder="ALLTHATPAY" maxLength={60} />
                        </Field>
                    </div>

                    <Field title="주소 키 *" hint="페이지 안 위치에 쓰입니다(/partners#allthatpay). 영문 소문자·숫자·하이픈.">
                        <input className={`${input} font-mono text-xs`} value={d.slug} onChange={set('slug')} placeholder="allthatpay" maxLength={60} />
                    </Field>

                    <Field title="로고" hint="PNG·JPG·WebP. 가로로 긴 로고는 여백 없이 잘라서 올리면 가장 잘 보입니다.">
                        <div className="flex items-center gap-3">
                            <div className="w-40 h-16 rounded-xl border border-gray-200 bg-white flex items-center justify-center p-2 shrink-0">
                                {d.logoUrl
                                    ? <img src={d.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                                    : <span className="text-[11px] text-gray-400">없음 — 이름으로 표시</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="px-3 py-1.5 rounded-lg bg-white border border-solid border-gray-300 text-gray-700 text-xs font-bold cursor-pointer hover:border-primary-500 text-center">
                                    {uploading ? '올리는 중…' : '파일 올리기'}
                                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={uploading} />
                                </label>
                                {d.logoUrl && (
                                    <button type="button" onClick={() => setD({ ...d, logoUrl: '' })} className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 hover:bg-red-50">
                                        로고 빼기
                                    </button>
                                )}
                            </div>
                        </div>
                    </Field>

                    <Field title="한 줄 소개 *">
                        <input className={input} value={d.tagline} onChange={set('tagline')} maxLength={160} />
                    </Field>

                    <Field title="소개 (선택)" hint="핵심 파트너는 2~4문장, 나머지는 1~2문장이 알맞습니다.">
                        <textarea className={`${input} min-h-[96px]`} value={d.summary} onChange={set('summary')} maxLength={2000} />
                    </Field>

                    <Field title={`특징 (한 줄에 하나, ${highlightCount}/8)`} hint="체크 표시와 함께 목록으로 보입니다.">
                        <textarea className={`${input} min-h-[120px]`} value={d.highlightsText} onChange={set('highlightsText')} />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field title="홈페이지 (선택)">
                            <input className={input} value={d.websiteUrl} onChange={set('websiteUrl')} placeholder="https://" />
                        </Field>
                        <Field title="연동 솔루션 (선택)" hint="고르면 '연동 솔루션 보기' 버튼이 생깁니다.">
                            <select className={input} value={d.solutionId} onChange={set('solutionId')}>
                                <option value="">없음</option>
                                {solutions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.slug || s.id} {s.isPublic === false ? '(비공개)' : ''}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field title="추가 링크 이름 (선택)" hint="예: 제품 보기">
                            <input className={input} value={d.linkLabel} onChange={set('linkLabel')} maxLength={30} />
                        </Field>
                        <Field title="추가 링크 주소 (선택)" hint="사이트 안 경로만. 예: /products">
                            <input className={`${input} font-mono text-xs`} value={d.linkPath} onChange={set('linkPath')} placeholder="/products" />
                        </Field>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
                        <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-700">
                            <input type="checkbox" checked={d.isKey} onChange={set('isKey')} className="w-4 h-4 accent-primary-500" />
                            핵심 파트너 <span className="font-normal text-xs text-gray-400">(같은 구분에서 맨 앞에 크게)</span>
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-700">
                            <input type="checkbox" checked={d.isPublished} onChange={set('isPublished')} className="w-4 h-4 accent-primary-500" />
                            공개
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-700">
                            순서
                            <input type="number" className={`${input} w-20`} value={d.sortOrder} onChange={set('sortOrder')} />
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-bold">취소</button>
                    <button
                        type="button" onClick={save} disabled={saving || uploading}
                        className="px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold disabled:opacity-40"
                    >{saving ? '저장 중…' : '저장'}</button>
                </div>
            </div>
        </div>
    );
};

const PartnerManagement = () => {
    const [rows, setRows] = useState([]);
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(null); // { id: number | 'new', draft }

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const [partners, sols] = await Promise.all([getAdminPartners(), getAdminSolutions().catch(() => [])]);
            setRows(partners);
            setSolutions(Array.isArray(sols) ? sols : []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setError('');
        try {
            await fn();
            await load();
        } catch (e) {
            setError(e.message);
        }
    };

    const remove = (p) => {
        if (!window.confirm(`'${p.name}' 을(를) 지울까요? 되돌릴 수 없습니다.\n\n잠시 내리기만 하려면 '숨김'을 쓰세요.`)) return;
        act(() => deletePartner(p.id));
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">파트너 관리</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        공개 페이지 <a href="/partners" target="_blank" rel="noopener noreferrer" className="font-bold text-primary-600 hover:underline">/partners</a> 에
                        보이는 시스템 연동 회사·ESL 제조사·설치용품 회사·고객 파트너입니다. 고객 파트너는 매장 설치를 전담하는 고객사만 넣습니다 — 일반 고객은 페이지 끝 문장으로 말합니다.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setEditing({ id: 'new', draft: EMPTY })}
                    className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold shrink-0"
                >+ 파트너 추가</button>
            </div>

            {error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900 whitespace-pre-line">{error}</div>
            )}

            {loading ? (
                <p className="py-12 text-center text-sm text-gray-400 animate-pulse">불러오는 중…</p>
            ) : (
                KINDS.map((k) => {
                    const items = rows.filter((r) => r.kind === k.value);
                    return (
                        <div key={k.value} className="mb-8">
                            <h3 className="text-sm font-extrabold text-gray-700 mb-2">{k.label} <span className="text-gray-400">{items.length}</span></h3>
                            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs">
                                            <tr>
                                                <th className="px-4 py-3 font-bold w-36">로고</th>
                                                <th className="px-4 py-3 font-bold">이름 · 한 줄 소개</th>
                                                <th className="px-4 py-3 font-bold w-40">연동 솔루션</th>
                                                <th className="px-4 py-3 font-bold w-16">순서</th>
                                                <th className="px-4 py-3 font-bold w-40 text-right">관리</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.length === 0 && (
                                                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-xs">
                                                    아직 없습니다. 없는 구분은 공개 페이지에서 보이지 않습니다.
                                                </td></tr>
                                            )}
                                            {items.map((p) => (
                                                <tr key={p.id} className={`border-b border-gray-100 last:border-0 ${p.isPublished ? '' : 'opacity-50'}`}>
                                                    <td className="px-4 py-3">
                                                        <div className="w-28 h-11 rounded-lg border border-gray-200 bg-white flex items-center justify-center p-1.5">
                                                            {p.logoUrl
                                                                ? <img src={p.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                                                                : <span className="text-[10px] text-gray-400">로고 없음</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="font-bold text-gray-900">
                                                            {p.name}
                                                            {p.isKey && <span className="ml-2 px-1.5 py-0.5 rounded bg-primary-500 text-white text-[10px] font-extrabold align-middle">핵심</span>}
                                                            {!p.isPublished && <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-bold align-middle">숨김</span>}
                                                            <span className="ml-2 font-mono text-[11px] font-normal text-gray-400">#{p.slug}</span>
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-0.5">{p.tagline}</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs">
                                                        {p.solutionId
                                                            ? (p.solutionPath
                                                                ? <a href={p.solutionPath} target="_blank" rel="noopener noreferrer" className="font-mono text-primary-600 hover:underline break-all">{p.solutionPath.split('/').pop()}</a>
                                                                : <span className="text-amber-700">비공개 솔루션</span>)
                                                            : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500">{p.sortOrder}</td>
                                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                                        <button type="button" onClick={() => setEditing({ id: p.id, draft: toDraft(p) })} className="px-2 py-1 text-xs font-bold text-gray-700 hover:text-primary-500">수정</button>
                                                        <button type="button" onClick={() => act(() => updatePartner(p.id, { isPublished: !p.isPublished }))} className="px-2 py-1 text-xs font-bold text-gray-700 hover:text-primary-500">
                                                            {p.isPublished ? '숨김' : '공개'}
                                                        </button>
                                                        <button type="button" onClick={() => remove(p)} className="px-2 py-1 text-xs font-bold text-red-600">삭제</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

            {editing && (
                <Editor
                    initial={editing.draft}
                    editId={editing.id}
                    solutions={solutions}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </AdminLayout>
    );
};

export default PartnerManagement;
